/** Pixel radii for Tailwind md+ / Bootstrap `.card` — keep in sync with `@theme` + TELLOPORT_RADIUS. */
export const SQUIRCLE_CLASS_RADIUS_PX = {
    'rounded-md': 8,
    'rounded-lg': 12,
    'rounded-xl': 16,
    'rounded-2xl': 20,
    'rounded-3xl': 24,
    /** Bootstrap `.card` / card-role shell — matches TELLOPORT_RADIUS.lg */
    card: 12
} as const;

export const SQUIRCLE_SURFACE_SELECTOR = ['.rounded-md', '.rounded-lg', '.rounded-xl', '.rounded-2xl', '.rounded-3xl', '[data-squircle-radius]'].join(',');

export type SquircleClassRadiusKey = keyof typeof SQUIRCLE_CLASS_RADIUS_PX;

/**
 * CSS `border` / `outline` + `clip-path` produce broken corner strokes.
 * Those surfaces stay G1 (`border-radius`) unless handled by `<Squircle>` + drop-shadow.
 *
 * Tailwind arbitrary properties (`[...]`) are ignored so token names like
 * `--color-border-default` inside a drop-shadow filter do not false-positive.
 */
export function hasCssStroke(el: HTMLElement): boolean {
    const className = el.className?.toString?.() ?? '';
    const withoutArbitrary = className.replace(/\[[^\]]*]/g, '');
    if (/(?:^|\s)border(?:-|\s|$)/.test(withoutArbitrary) || /(?:^|\s)outline(?:-|\s|$)/.test(withoutArbitrary)) {
        return true;
    }
    // Bootstrap `.card` always has a painted edge via theme tokens
    if (el.classList.contains('card')) {
        return true;
    }
    return false;
}

/** Smallest radius that still gets G2 squircle (Telloport md). */
export const SQUIRCLE_MIN_CORNER_RADIUS_PX = SQUIRCLE_CLASS_RADIUS_PX['rounded-md'];

/**
 * Resolves the G2 corner radius for an element, or null when it should stay G1
 * (explicit Squircle component, stroked edges, or no md+ radius class).
 */
export function resolveSquircleCornerRadius(el: Element): number | null {
    if (!(el instanceof HTMLElement)) {
        return null;
    }
    if (el.dataset['squircleManaged'] === 'component') {
        return null;
    }
    // Pane modals are flush to the edge
    if (el.classList.contains('modal-pane') || el.className.toString().includes('modal-pane')) {
        return null;
    }
    // Opt-in only when stroked — auto clip-path breaks border/outline rendering
    if (hasCssStroke(el) && el.dataset['squircleRadius'] === undefined) {
        return null;
    }

    const attr = el.dataset['squircleRadius'];
    if (attr !== undefined && attr !== '') {
        const parsed = Number(attr);
        return Number.isFinite(parsed) && parsed >= SQUIRCLE_MIN_CORNER_RADIUS_PX ? parsed : null;
    }

    if (el.classList.contains('rounded-3xl')) {
        return SQUIRCLE_CLASS_RADIUS_PX['rounded-3xl'];
    }
    if (el.classList.contains('rounded-2xl')) {
        return SQUIRCLE_CLASS_RADIUS_PX['rounded-2xl'];
    }
    if (el.classList.contains('rounded-xl')) {
        return SQUIRCLE_CLASS_RADIUS_PX['rounded-xl'];
    }
    if (el.classList.contains('rounded-lg')) {
        return SQUIRCLE_CLASS_RADIUS_PX['rounded-lg'];
    }
    if (el.classList.contains('rounded-md')) {
        return SQUIRCLE_CLASS_RADIUS_PX['rounded-md'];
    }

    return null;
}
